import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaRegClock, FaExclamationCircle, FaShieldAlt } from 'react-icons/fa';
import { RootState } from '../store/store';
import '../styles/TrialBanner.scss';
import {setAccountBannerHeight} from '../utility/accountBannerHeight';

// Full-width notice beneath the navbar. It shows ONE state, the first of these that applies:
//   1. Admin suspension -> warning bar: the account is suspended, contact support (no card CTA).
//   2. Payment recovery -> warning bar: unpaid invoices and the deadline, billing CTA.
//   3. Trial ended      -> warning bar: a no-card trial (tier === 'trial_no_card') is out of credit, card CTA. It names
//                          the welcome credit a card brings, and none when that is 0.
//   4. Trial active     -> accent bar: the no-card trial's credit and days left, card CTA.
//   5. Budget           -> accent bar: a card account that must have a monthly budget has none that blocks (none saved,
//                          or a legacy cap), Set budget CTA (BUDGET_MANDATORY_PLAN_2026-10-28 section 5.6). It goes as
//                          soon as a budget is saved.
// Any other account renders nothing, and no state can be dismissed.
//
// The bar is position:fixed directly beneath the fixed navbar. It publishes its live
// height into the --trial-banner-h CSS variable so the rest of the layout (content-container
// padding, category-page sidebar offset) shifts down and nothing renders behind it.

type BannerState = 'admin_suspended' | 'payment_recovery' | 'trial_ended' | 'trial_active' | 'budget';

const fmtCredits = (n: number): string => {
    const r = Math.round(n * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

/** A welcome credit in pounds: 200 reads 200, a part of a pound reads in pence (12.50). */
const fmtPounds = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const TrialBanner: React.FC = () => {
    const forecast = useSelector((state: RootState) => state.billingForecast.forecast);
    // Its own read (GET /v1/billing/budget), not the forecast. null when it failed or found no billing row: no banner.
    const budget = useSelector((state: RootState) => state.billingForecast.budget);
    const ref = useRef<HTMLDivElement>(null);

    // ⛔ An ADMIN suspension is not a trial running out, and until 2026-08-22 both rendered the same
    // banner because both write status = 'suspended'. Telling a suspended user to connect a card is
    // asking for payment details in exchange for something that will not happen: the suspension is
    // deliberately sticky, so no billing path lifts it.
    const adminSuspended = !!forecast?.suspended_by_admin;
    const recovery = forecast?.paymentRecovery;
    const recoveringPayment = !!recovery && !['observing', 'recovered', 'deleted'].includes(recovery.phase);
    // ⛔ The two trial states read the TIER, never the status alone: the invoice-policy suspension writes status
    // 'suspended' on a card account too, and a card account must never be told its trial ended.
    const trial = forecast?.tier === 'trial_no_card';
    const registrationPlan = forecast?.policy_version === 2;
    const freeResourcePlan = forecast?.policy_version === 3;
    const suspended = trial && (forecast?.status === 'suspended' || registrationPlan
        && ((forecast?.credits ?? 0) <= 0 || !!forecast?.credits_expires_at && forecast.credits_expires_at * 1000 <= Date.now())
        || freeResourcePlan && (!!forecast?.free_lifecycle?.suspended_at
            || !!forecast?.trial_expires_at && Date.parse(forecast.trial_expires_at)<=Date.now()));

    const budgetMissing = budget?.required === true && !budget.active;

    const state: BannerState | null =
        adminSuspended ? 'admin_suspended'
        : recoveringPayment ? 'payment_recovery'
        : trial && (forecast?.status === 'suspended' || suspended) ? 'trial_ended'
        // A trial must have a budget too (Milos, 2026-10-29), so a missing one comes before the trial's own bar.
        : budgetMissing ? 'budget'
        : trial ? 'trial_active'
        : null;
    const show = state !== null;

    useLayoutEffect(() => {
        const clear = () => setAccountBannerHeight('primary', 0);
        if (!show || !ref.current) {
            clear();
            return clear;
        }
        const el = ref.current;
        const apply = () => setAccountBannerHeight('primary', el.offsetHeight);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => {
            ro.disconnect();
            clear();
        };
    }, [show, state, forecast?.credits, forecast?.credits_expires_at, forecast?.welcome_credit_gbp, budget?.legacy]);

    if (!state) return null;

    // An account suspended by redu. No card CTA: a card does not lift this, and offering one would
    // take payment details for an outcome that cannot follow.
    if (state === 'admin_suspended') {
        return (
            <div ref={ref} className="trial-banner trial-banner--suspended" role="alert">
                <FaExclamationCircle className="trial-banner__icon" />
                <span className="trial-banner__text">
                    <strong>This account is suspended and its resources are offline.</strong>{' '}
                    If you believe this is a mistake, contact us at <strong>support@redu.cloud</strong> and we
                    will look into it.
                </span>
                <a href="mailto:support@redu.cloud?subject=Account%20suspension%20appeal" className="trial-banner__cta">
                    Contact support
                </a>
            </div>
        );
    }

    if (state === 'payment_recovery' && recovery) {
        return (
            <div ref={ref} className="trial-banner trial-banner--suspended" role="alert">
                <FaExclamationCircle className="trial-banner__icon" />
                <span className="trial-banner__text">
                    {recovery.phase === 'recovering' ? 'Payment received. Your services are being restored.' :
                        recovery.phase === 'deleting' ? 'Account cleanup has started. Contact support about any recent payment.' :
                        ['suspending', 'suspended'].includes(recovery.phase) && recovery.deletionAt ?
                            <>Services are suspended for unpaid invoices. Account and resource deletion begins <strong>{new Date(recovery.deletionAt).toLocaleString()}</strong>. Settle all overdue invoices before then to recover.</> :
                            <>Payment failed. Existing services remain running until <strong>{recovery.deletionAt ? new Date(recovery.deletionAt).toLocaleString() : 'the payment deadline'}</strong>. Settle all overdue invoices before then to prevent suspension and deletion.{recovery.nextRetryAt ? ` Stripe's next retry is ${new Date(recovery.nextRetryAt).toLocaleString()}.` : ''}</>}
                </span>
                <Link to="/category/user/billing" className="trial-banner__cta">Open billing</Link>
            </div>
        );
    }

    if (freeResourcePlan && forecast && trial) {
        const deadline=forecast.trial_expires_at ? Date.parse(forecast.trial_expires_at) : NaN;
        const daysLeft=Number.isFinite(deadline)?Math.max(0,Math.ceil((deadline-Date.now())/86400000)):null;
        return <div ref={ref} className={'trial-banner '+(suspended?'trial-banner--suspended':'trial-banner--active')}
            role={suspended?'alert':'status'}>
            <FaRegClock className="trial-banner__icon" />
            <span className="trial-banner__text">{suspended ? <>
                <strong>Your free-resource trial has ended.</strong>{' '}
                Free VMs are scheduled for deletion. Connect a card to keep remaining VMs; data already deleted cannot be recovered.
            </> : <><strong>{daysLeft!==null ? `${daysLeft} ${daysLeft===1?'day':'days'} left` : '7-day trial'}</strong>. Connect a card or your free VMs and data will be deleted.</>}</span>
            <Link to="/category/user/billing?setup-billing=1" className="trial-banner__cta">Connect card</Link>
        </div>;
    }

    if (registrationPlan && suspended) {
        return <div ref={ref} className="trial-banner trial-banner--suspended" role="alert">
            <span className="trial-banner__text">Your trial has ended. Trial resources are scheduled for deletion or have been deleted; your account remains available. Adding a card cannot restore resources already deleted. Late activation does not earn an activation bonus.</span>
            <Link to="/category/user/billing?setup-billing=1" className="trial-banner__cta">Connect card</Link>
        </div>;
    }

    if (state === 'trial_ended') {
        // The welcome credit a card brings (the server's CARDED_WELCOME_CREDIT_GBP), from the forecast: the very read that
        // decided this state, so the amount is there whenever this banner is, never a second read that lands later or
        // fails alone. Named only when it is a number above 0: at 0 there is none, and an amount not known promises
        // nothing rather than a credit that may not come.
        const welcomeCredit = forecast?.welcome_credit_gbp;
        return (
            <div ref={ref} className="trial-banner trial-banner--suspended" role="alert">
                <FaExclamationCircle className="trial-banner__icon" />
                <span className="trial-banner__text">
                    <strong>Your limited trial has ended and your resources are paused.</strong>{' '}
                    {typeof welcomeCredit === 'number' && welcomeCredit > 0
                        ? <>Connect your card to resume where you left off and receive <strong>£{fmtPounds(welcomeCredit)}</strong> in credits.</>
                        : 'Connect your card to resume where you left off.'}
                </span>
                <Link to="/category/user/billing?setup-billing=1" className="trial-banner__cta">
                    Connect card
                </Link>
            </div>
        );
    }

    // A legacy cap is stored but blocks nothing, so the person confirms it rather than sets one.
    if (state === 'budget') {
        return (
            <div ref={ref} className="trial-banner trial-banner--active">
                <FaShieldAlt className="trial-banner__icon" />
                <span className="trial-banner__text">
                    <strong>{budget?.legacy ? 'Confirm your monthly budget for this account.' : 'Set a monthly budget for this account.'}</strong>{' '}
                    We email you as usage approaches it, and you cannot create new resources once it is reached.
                </span>
                <Link to="/category/user/billing?setup-budget=1" className="trial-banner__cta">
                    Set budget
                </Link>
            </div>
        );
    }

    const credits = fmtCredits(forecast?.credits ?? 0);
    const expiresAt = forecast?.credits_expires_at ?? null;
    const daysLeft = expiresAt
        ? Math.max(0, Math.ceil((expiresAt * 1000 - Date.now()) / 86400000))
        : null;

    return (
        <div ref={ref} className="trial-banner trial-banner--active">
            <FaRegClock className="trial-banner__icon" />
            <span className="trial-banner__text">
                <strong>£{credits} trial credit</strong>
                {daysLeft !== null ? <> · {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left</> : null}
            </span>
            <Link to="/category/user/billing?setup-billing=1" className="trial-banner__cta">
                Connect card
            </Link>
        </div>
    );
};

export default TrialBanner;
